import { mergeDiff3 } from "node-diff3";

// Parse the streaming XML stream into structured chunks.
// Recognizes <explanation>...</explanation>, <file path="...">...</file>, and <plan>{...}</plan>.
export function parseStreamedXml(raw) {
  const files = {};
  let explanation = "";
  let plan = null;

  const fileRe = /<file\s+path=(?:"([^"]+)"|'([^']+)')\s*>([\s\S]*?)<\/file>/g;
  let match;
  while ((match = fileRe.exec(raw)) !== null) {
    const path = match[1] || match[2];
    files[path] = (match[3] || "").replace(/^\n/, "").replace(/\n\s*$/, "\n");
  }

  const explanationRe = /<explanation>([\s\S]*?)<\/explanation>/g;
  const parts = [];
  while ((match = explanationRe.exec(raw)) !== null) {
    parts.push(match[1].trim());
  }
  explanation = parts.join("\n\n");

  const planMatch = /<plan>([\s\S]*?)<\/plan>/.exec(raw);
  if (planMatch) {
    try {
      plan = JSON.parse(planMatch[1]);
    } catch (e) {
      const fallback = planMatch[1].match(/\{[\s\S]*\}/);
      if (fallback) {
        try {
          plan = JSON.parse(fallback[0]);
        } catch (_) {
          plan = null;
        }
      }
    }
  }

  return { files, explanation, plan };
}

// SSE fetch helper — streams tokens through an onToken callback.
export async function streamOmniCraft(url, token, body, onEvent, signal) {
  const res = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Token": token,
      Accept: "text/event-stream",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(detail || `Stream failed with ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "token";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    // SSE spec allows LF, CR, or CRLF terminators — sse-starlette uses CRLF.
    while (
      (idx = (() => {
        const a = buffer.indexOf("\r\n\r\n");
        const b = buffer.indexOf("\n\n");
        if (a === -1) return b;
        if (b === -1) return a;
        return Math.min(a, b);
      })()) !== -1
    ) {
      const isCrlf = buffer.slice(idx, idx + 4) === "\r\n\r\n";
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + (isCrlf ? 4 : 2));
      let event = currentEvent;
      const dataLines = [];
      for (const line of raw.split(/\r?\n/)) {
        if (line.startsWith("event:")) {
          event = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).replace(/^\s/, ""));
        }
      }
      const data = dataLines.join("\n");
      currentEvent = event;
      onEvent(event, data);
    }
  }
}

// 3-way merge — returns { ok, text, conflicts }.
export function reconcile3Way(base, user, ai) {
  if (base === user) return { ok: true, text: ai };
  if (base === ai) return { ok: true, text: user };
  if (user === ai) return { ok: true, text: ai };
  try {
    const result = mergeDiff3(user.split("\n"), base.split("\n"), ai.split("\n"), {
      label: { a: "yours", o: "base", b: "ai" },
    });
    if (!result.conflict) {
      return { ok: true, text: result.result.join("\n") };
    }
    return { ok: false, text: result.result.join("\n"), conflicts: true };
  } catch (e) {
    return { ok: false, text: ai, conflicts: true };
  }
}

export function planFingerprint(plan) {
  if (!plan) return "";
  return (plan.summary || "") + (plan.affectedFiles || []).map((f) => f.path).join("|");
}
